import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Sidebar from "../Sidebar/Sidebar";
import "./TeamRequests.css";
import { updatePilotStatus } from "../Apis/AdminApis";
import PopupMessage from "../PopupMessage/PopupMessage";
import pavamanLogo from "../images/drone.png";

const PilotDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [pilot, setPilot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [popupType, setPopupType] = useState("info");
  const [actionLoading, setActionLoading] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [isActionProcessing, setIsActionProcessing] = useState(false);
  
  const state = location.state;
  const fromPage = state?.from || "active";

useEffect(() => {
  const state = location.state;
  
  // Check for session expiry in navigation state
  if (state?.sessionExpired) {
    setPopupMessage("Session expired, redirecting to login page");
    setPopupType("error");
    setPopupOpen(true);
    setTimeout(() => {
      navigate("/login");
    }, 2000);
    return;
  }
  
  if (state && state.pilot) {
    setPilot(state.pilot);
    setLoading(false);
  } else {
    navigate(fromPage === "requests" 
      ? "/admin/teams/requests" 
      : "/admin/teams/active"
    );
  }
}, [location, navigate, fromPage]);

  const formatMobile = (mobile) => {
    if (!mobile) return "N/A";
    return mobile.startsWith('+91') ? mobile : `+91 ${mobile}`;
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "Never";
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Never";
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

const handleStatusUpdate = async (action) => {
  try {
    setIsActionProcessing(true);
    
    const adminApprovedValue = action === 'approve' ? 1 : 2;
    
    const payload = {
      pilot_email: pilot.email,
      admin_approved: adminApprovedValue,
    };

    const res = await updatePilotStatus(payload);

    // Session expiry check
    if (res?.error) {
      if (res?.error?.includes("Unauthorized")) {
        setPopupMessage("Session expired, redirecting to login page");
        setPopupType("error");
        setPopupOpen(true);
        setTimeout(() => {
          navigate("/login");
        }, 2000);
        return;
      }
      
      setPopupMessage(res.error);
      setPopupType("error");
      setPopupOpen(true);
      setIsActionProcessing(false);
      return;
    }

    const successMessage = res?.message || 
      (action === 'approve' ? "Pilot approved successfully" : "Pilot rejected and device blocked successfully");
    
    setPopupMessage(successMessage);
    setPopupType("success");
    setPopupOpen(true);

    setShowActionModal(false);
    setActionType(null);
    setTimeout(() => {
      navigate(fromPage === "requests" ? "/admin/teams/requests" : "/admin/teams/active");
    }, 1500);

  } catch (err) {
    console.error(err);
    
    // Check for unauthorized error in catch block
    if (err?.response?.status === 401 || err?.response?.data?.error?.includes("Unauthorized")) {
      setPopupMessage("Session expired, redirecting to login page");
      setPopupType("error");
      setPopupOpen(true);
      setTimeout(() => {
        navigate("/login");
      }, 2000);
      return;
    }
    
    setPopupMessage(err?.response?.data?.error || "Failed to update pilot status");
    setPopupType("error");
    setPopupOpen(true);
  } finally {
    setIsActionProcessing(false);
  }
};

  const openActionModal = (type) => {
    setActionType(type);
    setShowActionModal(true);
  };

  const closeActionModal = () => {
    if (isActionProcessing) return;
    setShowActionModal(false);
    setActionType(null);
  };

  const handleGoBack = () => {
    navigate(fromPage === "requests" ? "/admin/teams/requests" : "/admin/teams/active");
  };

  if (loading) {
    return (
      <div className="dashboard-wrapper">
        <Sidebar />
        <div className="dashboard-content">
          <div className="custom-loading-container">
            <img src={pavamanLogo} alt="Loading" className="spinning-logo" />
            <p className="loading-text">Loading pilot details...</p>
          </div>
        </div>
      </div>
    );
  }

if (!pilot) {
  return (
    <div className="dashboard-wrapper">
      <Sidebar />
      <div className="dashboard-content">
        <div className="tr-no-results">
          <div className="tr-no-results-icon">👨‍✈️</div>
          <h3>Pilot not found</h3>
          <p>The pilot information could not be loaded. Please try again.</p>
          <button className="tr-modal-close-btn" onClick={handleGoBack}>
            Go Back
          </button>
        </div>
      </div>
    </div>
  );
}

  const isFromRequests = fromPage === "requests";
  const isFromActive = fromPage === "active";
  const showApproveReject = isFromRequests && pilot.status === "pending";
  const showDeactivate = isFromActive && pilot.status === "active";
  const showRejectOnly = isFromRequests && pilot.status === "approved";

  return (
    <div className="dashboard-wrapper">
      <Sidebar />
      
      <PopupMessage
        isOpen={popupOpen}
        onClose={() => setPopupOpen(false)}
        message={popupMessage}
        type={popupType}
      />

      <div className="dashboard-content">
        <div className="dashboard-header">
          <div className="header-left">
            <h1 className="dashboard-title">Pilot Details</h1>
            <p className="dashboard-subtitle">View and manage pilot information</p>
          </div>
          <div className="header-right">
            <button className="back-btn" onClick={handleGoBack}>
              ← Back to {fromPage === "requests" ? "Team Requests" : "Active Teams"}
            </button>
          </div>
        </div>

        <div className="tr-details-page">
          <div className="tr-details-grid">
            <div className="tr-detail-section">
              <h3>Basic Information</h3>
              <div className="tr-detail-item">
                <span className="tr-detail-label">Full Name:</span>
                <span className="tr-detail-value">{pilot.name}</span>
              </div>
              <div className="tr-detail-item">
                <span className="tr-detail-label">Email:</span>
                <span className="tr-detail-value">{pilot.email}</span>
              </div>
              <div className="tr-detail-item">
                <span className="tr-detail-label">Mobile:</span>
                <span className="tr-detail-value">{formatMobile(pilot.mobile_no)}</span>
              </div>
            </div>

            <div className="tr-detail-section">
              <h3>Login Information</h3>
              <div className="tr-detail-item">
                <span className="tr-detail-label">Account Status:</span>
                <span className="tr-detail-value tr-status-badge" style={{ 
                  background: pilot.statusBg, 
                  color: pilot.statusColor 
                }}>
                  {pilot.statusIcon} {pilot.statusLabel || (pilot.status === "active" ? "Active" : pilot.status === "pending" ? "Pending" : pilot.status === "rejected" ? "Rejected" : "Blocked")}
                </span>
              </div>
              <div className="tr-detail-item">
                <span className="tr-detail-label">Login Status:</span>
                <span className={`tr-detail-value ${pilot.login_status === true || pilot.login_status === 1 ? "tr-online" : "tr-offline"}`}>
                  {pilot.login_status === true || pilot.login_status === 1 ? "Online" : "Offline"}
                </span>
              </div>
              <div className="tr-detail-item">
                <span className="tr-detail-label">Last Login:</span>
                <span className="tr-detail-value">{formatDateTime(pilot.login_on)}</span>
              </div>
              <div className="tr-detail-item">
                <span className="tr-detail-label">Registered On:</span>
                <span className="tr-detail-value">{formatDateTime(pilot.created_on)}</span>
              </div>
            </div>

            <div className="tr-detail-section">
              <h3>Device Information</h3>
              <div className="tr-detail-item">
                <span className="tr-detail-label">Total Devices:</span>
                <span className="tr-detail-value">{pilot.device_count || 0}</span>
              </div>
              <div className="tr-detail-item">
                <span className="tr-detail-label">Approved:</span>
                <span className="tr-detail-value tr-text-approved">
                  {pilot.approved_count || 0}
                </span>
              </div>
              <div className="tr-detail-item">
                <span className="tr-detail-label">Pending:</span>
                <span className="tr-detail-value" style={{ color: "#FFA500" }}>
                  {pilot.pending_devices?.length || 0}
                </span>
              </div>
              {/* <div className="tr-detail-item">
                <span className="tr-detail-label">Usage Device:</span>
                <span className="tr-detail-value">{pilot.device_id}</span>
              </div> */}
            </div>

            {pilot.approved_devices && pilot.approved_devices.length > 0 && (
              <div className="tr-detail-section tr-device-list">
                <h3>Approved Devices</h3>
                {pilot.approved_devices.map((device, index) => (
                  <div key={index} className="tr-device-item" style={{ 
                    padding: '10px', 
                    marginBottom: '8px', 
                    background: '#D4EDDA',
                    borderRadius: '8px'
                  }}>
                    <div className="tr-detail-item">
                      <span className="tr-detail-label">IP Address:</span>
                      <span className="tr-detail-value">{device.ip_address || 'N/A'}</span>
                    </div>
                    <div className="tr-detail-item">
                      <span className="tr-detail-label">Usage Device:</span>
                      <span className="tr-detail-value">{device.is_active ? 'Yes' : 'No'}</span>
                    </div>
                    <div className="tr-detail-item">
                      <span className="tr-detail-label">Approved On:</span>
                      <span className="tr-detail-value">{formatDate(device.changed_on || device.created_on)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="tr-detail-section">
              <h3>Quick Actions</h3>
              <div className="tr-modal-actions">
                {showApproveReject && (
                  <>
                    <button 
                      className="tr-modal-btn tr-btn-approve" 
                      onClick={() => openActionModal('approve')}
                      disabled={actionLoading}
                    >
                      ✓ Approve Pilot
                    </button>
                    <button 
                      className="tr-modal-btn tr-btn-reject" 
                      onClick={() => openActionModal('reject')}
                      disabled={actionLoading}
                    >
                      ✗ Reject Pilot
                    </button>
                  </>
                )}
                {showDeactivate && (
                  <button 
                    className="tr-modal-btn tr-btn-block" 
                    onClick={() => openActionModal('deactivate')}
                    disabled={actionLoading}
                  >
                    🚫 Deactivate Pilot
                  </button>
                )}
                {showRejectOnly && (
                  <button 
                    className="tr-modal-btn tr-btn-reject" 
                    onClick={() => openActionModal('reject')}
                    disabled={actionLoading}
                  >
                    ✗ Reject & Block
                  </button>
                )}
                {pilot.pending_devices?.length > 0 && fromPage === "requests" && pilot.status === "approved" && (
                  <button 
                    className="tr-modal-btn tr-btn-approve" 
                    onClick={() => openActionModal('approve')}
                    disabled={actionLoading}
                  >
                    ✓ Approve Pending Device
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {showActionModal && (
        <div className="tr-modal-overlay" onClick={closeActionModal}>
          <div className="tr-modal-content tr-modal-small" onClick={(e) => e.stopPropagation()}>
            <div className="tr-modal-header">
              <h2>
                {actionType === 'approve' ? 'Approve Pilot' : 
                 actionType === 'deactivate' ? 'Deactivate Pilot' : 'Reject Pilot'}
              </h2>
              <button className="tr-modal-close" onClick={closeActionModal} disabled={isActionProcessing}>✕</button>
            </div>
            <div className="tr-modal-body">
              <div className="tr-confirm-container">
                <div className={`tr-confirm-icon ${
                  actionType === 'approve' ? 'tr-confirm-approve' : 'tr-confirm-reject'
                }`}>
                  {actionType === 'approve' ? '✓' : '✗'}
                </div>
                <p className="tr-confirm-text">
                  Are you sure you want to {actionType === 'approve' ? 'approve' : actionType === 'deactivate' ? 'deactivate' : 'reject'} <strong>{pilot?.name}</strong>?
                </p>
                <p className="tr-confirm-subtext">
                  {actionType === 'approve' 
                    ? 'The pilot will be able to access the platform and use their device.' 
                    : actionType === 'deactivate'
                    ? 'The pilot will be deactivated and their device will be blocked. They will not be able to access the platform until reactivated.'
                    : 'The pilot will be rejected and their device will be blocked. They will not be able to access the platform.'}
                </p>
                <div className="tr-confirm-buttons">
                  <button 
                    className="tr-confirm-cancel" 
                    onClick={closeActionModal}
                    disabled={isActionProcessing}
                  >
                    Cancel
                  </button>
                  <button 
                    className={actionType === 'approve' ? 'tr-confirm-approve-btn' : 'tr-confirm-reject-btn'}
                    onClick={() => handleStatusUpdate(actionType === 'deactivate' ? 'reject' : actionType)}
                    disabled={isActionProcessing}
                  >
                    {isActionProcessing ? (
                      <>
                        <span className="tr-spinner-small"></span>
                        Processing...
                      </>
                    ) : (
                      `Yes, ${actionType === 'approve' ? 'Approve' : actionType === 'deactivate' ? 'Deactivate' : 'Reject'}`
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PilotDetails;